import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, Building2, Heart } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EVENT_CONFIG, API, fmtUSD } from "@/config/eventConfig";
import { GoldButton } from "./shared";

const DonateModal = ({ open, prefill, onClose }) => {
  const [amount, setAmount] = useState(null);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one-time");

  useEffect(() => {
    if (open) {
      setAmount(prefill || null);
      setCustom("");
      setFrequency("one-time");
    }
  }, [open, prefill]);

  const chosen = custom ? Number(custom) : amount;

  const submit = async () => {
    if (!chosen || chosen <= 0) {
      toast.error("Choose or enter an amount to build a crown.");
      return;
    }
    try {
      await axios.post(`${API}/donation-intents`, { amount: chosen, frequency });
    } catch (e) {
      /* analytics-ready intent capture is best-effort */
    }
    const url = frequency === "monthly" ? EVENT_CONFIG.urls.donateMonthly : EVENT_CONFIG.urls.donate;
    toast.success(`Opening Styles 4 Kidz secure giving — ${fmtUSD(chosen)} ${frequency}.`);
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="donation-modal"
        >
          <div className="absolute inset-0 bg-ink/85 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-coal gold-stroke grain p-7 sm:p-10"
          >
            <button
              data-testid="donation-modal-close-button"
              onClick={onClose}
              className="absolute top-5 right-5 text-cream/60 hover:text-champagne transition-colors"
              aria-label="Close donation modal"
            >
              <X size={22} />
            </button>

            <p className="font-script text-3xl text-champagne">Give with heart</p>
            <h3 className="font-serif text-2xl sm:text-3xl text-ivory mt-2 tracking-tight">
              WHAT WILL YOUR CROWN BUILD?
            </h3>

            <div
              className="mt-6 grid grid-cols-2 rounded-full border border-champagne/30 overflow-hidden"
              role="tablist"
            >
              {["one-time", "monthly"].map((f) => (
                <button
                  key={f}
                  data-testid={`donation-modal-toggle-${f}`}
                  onClick={() => setFrequency(f)}
                  className={`py-3 text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold transition-colors ${
                    frequency === f ? "bg-champagne text-ink" : "text-cream hover:text-champagne"
                  }`}
                >
                  {f === "one-time" ? "One-Time" : "Monthly"}
                </button>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              {EVENT_CONFIG.donationAmounts.map((a) => (
                <button
                  key={a}
                  data-testid={`donation-amount-preset-${a}`}
                  onClick={() => {
                    setAmount(a);
                    setCustom("");
                  }}
                  className={`py-3.5 rounded-xl text-sm font-semibold tracking-wide transition-all duration-200 border ${
                    amount === a && !custom
                      ? "bg-champagne text-ink border-champagne shadow-[0_8px_25px_-8px_rgba(230,199,134,0.6)]"
                      : "border-white/10 text-cream hover:border-champagne/50 hover:text-champagne"
                  }`}
                >
                  {a >= 100000 ? "$100K+" : fmtUSD(a)}
                </button>
              ))}
            </div>

            <div className="mt-4">
              <input
                data-testid="donation-custom-amount-input"
                type="number"
                min="1"
                inputMode="numeric"
                placeholder="Other amount"
                value={custom}
                onChange={(e) => {
                  setCustom(e.target.value);
                  setAmount(null);
                }}
                className="w-full bg-ink/60 border border-white/10 rounded-xl px-5 py-4 text-ivory placeholder:text-cream/40 focus:outline-none focus:border-champagne/60 text-lg"
              />
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <GoldButton
                testid="donation-modal-submit-button"
                onClick={submit}
                className="w-full pulse-gold"
              >
                <Crown size={16} /> Give {chosen ? fmtUSD(chosen) : "Now"} {frequency === "monthly" ? "Monthly" : ""}
              </GoldButton>
              <div className="flex flex-col sm:flex-row gap-3 text-center">
                <a
                  data-testid="donation-modal-corporate-link"
                  href={`mailto:${EVENT_CONFIG.org.email}?subject=Corporate%20Giving%20—%20Building%20Crowns%204%20The%20Future`}
                  className="flex-1 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] text-cream/70 hover:text-champagne py-2 transition-colors"
                >
                  <Building2 size={14} /> Corporate Giving
                </a>
                <a
                  data-testid="donation-modal-major-gift-link"
                  href={`mailto:${EVENT_CONFIG.org.email}?subject=Major%20Gift%20—%20Building%20Crowns%204%20The%20Future`}
                  className="flex-1 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] text-cream/70 hover:text-champagne py-2 transition-colors"
                >
                  <Heart size={14} /> Make a Major Gift
                </a>
              </div>
              <p className="text-center text-[11px] text-cream/40 tracking-wide">
                You will complete your gift on Styles 4 Kidz' verified secure giving page.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DonateModal;
